import axios, { AxiosError } from "axios"
import { useMutation, UseMutationOptions } from "@tanstack/react-query"

import { BulkLabel, BulkPart } from "@app/services/types"
import { useAddGlobalToast } from "@app/providers/GlobalToastProvider"
import { extractApiErrorMessage } from "@app/utils/extractApiErrorMessage"

export const ERROR_TITLE = "Error Generating Bulk Labels"
export const ERROR_MESSAGE = "There was an error generating bulk labels. Contact support if the issue persists."

export type BulkLabelsReqBody = Omit<BulkPart, "part_name" | "search"> & {
	part_number: string
	count: number
	description: string
}

export type CreateBulkLabelsRequest = {
	bulk: BulkLabelsReqBody[]
}

export type CreateBulkLabelsResponse = {
	bulk_labels: BulkLabel[]
}

const createBulkLabels = async (payload: CreateBulkLabelsRequest) => {
	return axios.post<CreateBulkLabelsResponse>("skunkworks/bulk-labels", payload).then((response) => response.data)
}

export const useCreateBulkLabels = (
	mutationOptions: Omit<
		UseMutationOptions<CreateBulkLabelsResponse, Error, CreateBulkLabelsRequest>,
		"mutationFn"
	> = {}
) => {
	const addToast = useAddGlobalToast()

	return useMutation({
		mutationFn: (payload: CreateBulkLabelsRequest) => createBulkLabels(payload),
		onError: (error: AxiosError) => {
			addToast({
				message: `${ERROR_TITLE}: ${extractApiErrorMessage(error, ERROR_MESSAGE)}`,
				severity: "error",
			})
		},
		...mutationOptions,
	})
}

export default useCreateBulkLabels
